 /**
 * CoralPlacerApp — the native Coral Placer page that replaces the old
 * compiled iframe app. It owns the merged catalog (bundled data + the
 * operator's saved edits), the shared filter state, and the three views
 * (Interactive Guide, Coral Placer canvas, Collection Manager). The
 * editable coral pop-up is shared across every view; placements and edits
 * are saved to the Pi via `useWorkspace` / `useCoralEdits`.
 */
import { useMemo, useState } from 'react';

import {
  applyEdits,
  filterAndSort,
  findCoral,
  loadCatalog,
} from './catalog';
import { CollectionManager } from './CollectionManager';
import { CoralDetailModal } from './CoralDetailModal';
import { FiltersPanel } from './FiltersPanel';
import { InteractiveGuide } from './InteractiveGuide';
import { PlacerCanvas } from './PlacerCanvas';
import { useCoralEdits, useSavedLayouts, useWorkspace } from './persistence';
import {
  clearCoralEdit,
  placementsFor,
  setActiveAquascape,
  setCoralEdit,
  setPlacementsFor,
} from './workspace';
import { addSavedLayout, findSavedLayout, removeSavedLayout } from './savedLayouts';
import { genId } from './ids';
import type {
  ColorTag,
  CoralPlacerView,
  CoralEdit,
  FilterState,
  GrowthForm,
  GuideLayout,
} from './types';

const DEFAULT_FILTERS: FilterState = {
  query: '',
  colors: [],
  growthForms: [],
  sort: 'name-asc',
};

const VIEW_TABS: ReadonlyArray<{ id: CoralPlacerView; label: string }> = [
  { id: 'guide', label: 'Interactive Guide' },
  { id: 'placer', label: 'Coral Placer' },
  { id: 'collection', label: 'Collection Manager' },
];

const GUIDE_LAYOUTS: ReadonlyArray<{ id: GuideLayout; label: string }> = [
  { id: 'cards', label: 'Cards' },
  { id: 'list', label: 'Compact list' },
  { id: 'mindmap', label: 'Mind map' },
];

export function CoralPlacerApp() {
  const catalog = useMemo(() => loadCatalog(), []);
  const { edits, setEdits } = useCoralEdits();
  const { workspace, setWorkspace } = useWorkspace();
  const { layouts, setLayouts } = useSavedLayouts();

  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [view, setView] = useState<CoralPlacerView>('guide');
  const [guideLayout, setGuideLayout] = useState<GuideLayout>('cards');
  const [selectedCoralId, setSelectedCoralId] = useState<string | null>(null);

  const mergedCatalog = useMemo(
    () => applyEdits(catalog, edits),
    [catalog, edits],
  );
  const filteredCorals = useMemo(
    () => filterAndSort(mergedCatalog, filters),
    [mergedCatalog, filters],
  );
  const editedIds = useMemo(() => new Set(Object.keys(edits)), [edits]);

  const selectedCoral = selectedCoralId
    ? (findCoral(mergedCatalog, selectedCoralId) ?? null)
    : null;

  const handleSaveEdit = (coralId: string, edit: CoralEdit) => {
    setEdits(setCoralEdit(edits, coralId, edit));
  };
  const handleRevert = (coralId: string) => {
    setEdits(clearCoralEdit(edits, coralId));
  };
  const handleColorFilter = (color: ColorTag) => {
    setFilters((f) => ({ ...f, colors: [color] }));
    setGuideLayout('cards');
  };
  const handleGrowthFilter = (form: GrowthForm) => {
    setFilters((f) => ({ ...f, growthForms: [form] }));
    setGuideLayout('cards');
  };
  const handleSaveLayout = (name: string) => {
    const trimmed = name.trim();
    if (trimmed === '') return;
    setLayouts(
      addSavedLayout(layouts, {
        id: genId('layout'),
        name: trimmed,
        aquascapeId: workspace.activeAquascapeId,
        placements: [...placementsFor(workspace, workspace.activeAquascapeId)],
        createdAt: Date.now(),
      }),
    );
  };
  const handleLoadLayout = (id: string) => {
    const layout = findSavedLayout(layouts, id);
    if (!layout) return;
    const targetAq = workspace.aquascapes.some((a) => a.id === layout.aquascapeId)
      ? layout.aquascapeId
      : workspace.activeAquascapeId;
    const next = setActiveAquascape(
      setPlacementsFor(workspace, targetAq, layout.placements),
      targetAq,
    );
    setWorkspace(next);
  };
  const handleDeleteLayout = (id: string) => {
    setLayouts(removeSavedLayout(layouts, id));
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header: view tabs + count + guide layout toggle */}
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg bg-bg-elevated p-1 ring-1 ring-surface-stroke">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setView(tab.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                view === tab.id
                  ? 'bg-accent text-white'
                  : 'text-text-2 hover:text-text-1'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-text-3">
            {filteredCorals.length} of {mergedCatalog.length} corals
          </span>
          {view === 'guide' && (
            <div className="flex items-center gap-1 rounded-lg bg-bg-elevated p-1 ring-1 ring-surface-stroke">
              {GUIDE_LAYOUTS.map((layout) => (
                <button
                  key={layout.id}
                  type="button"
                  onClick={() => setGuideLayout(layout.id)}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    guideLayout === layout.id
                      ? 'bg-accent text-white'
                      : 'text-text-2 hover:text-text-1'
                  }`}
                >
                  {layout.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Body: filters rail + active view */}
      <div className="flex min-h-0 flex-1 gap-4">
        <aside className="hidden w-56 flex-shrink-0 overflow-y-auto md:block">
          <FiltersPanel filters={filters} onChange={setFilters} />
        </aside>

        <main className="min-h-0 min-w-0 flex-1">
          {view === 'guide' && (
            <InteractiveGuide
              corals={filteredCorals}
              layout={guideLayout}
              onSelectCoral={setSelectedCoralId}
              onColorFilter={handleColorFilter}
              onGrowthFilter={handleGrowthFilter}
            />
          )}
          {view === 'placer' && (
            <PlacerCanvas
              corals={mergedCatalog}
              filteredCorals={filteredCorals}
              workspace={workspace}
              setWorkspace={setWorkspace}
              savedLayouts={layouts}
              onSaveLayout={handleSaveLayout}
              onLoadLayout={handleLoadLayout}
              onDeleteLayout={handleDeleteLayout}
            />
          )}
          {view === 'collection' && (
            <CollectionManager
              corals={filteredCorals}
              editedIds={editedIds}
              onSelectCoral={setSelectedCoralId}
            />
          )}
        </main>
      </div>

      <CoralDetailModal
        open={selectedCoral !== null}
        coral={selectedCoral}
        hasEdit={selectedCoralId !== null && editedIds.has(selectedCoralId)}
        onClose={() => setSelectedCoralId(null)}
        onSave={handleSaveEdit}
        onRevert={handleRevert}
      />
    </div>
  );
}
